import {vec2, mat2d, mat4, ReadonlyMat4} from 'gl-matrix';
import { TransformsStyle } from 'react-native';

export * from 'gl-matrix';

const extraVec2Methods = {
  x: (v: vec2) => v[0],
  y: (v: vec2) => v[1],
  setX: (v: vec2, x: number) => v[0] = x,
  setY: (v: vec2, y: number) => v[1] = y,
  unit: (n: number, out: vec2 = vec2.create()) => {
    return vec2.set(out, n, n);
  },

  toSize: (v: vec2) => ({ width: v[0], height: v[1] }),
  toXY: (v: vec2) => ({ x: v[0], y: v[1] }),
  fromSize: (size: {width: number, height: number}) => vec2.fromValues(size.width, size.height),
  fromXY: (xy: {x: number, y: number}) => vec2.fromValues(xy.x, xy.y),

  transformSize: (out: vec2, v: vec2, xf: mat2d) => {
    const zero = vec2.zero(vec2.create());
    vec2.transformMat2d(zero, zero, xf);
    const extent = vec2.clone(v);
    vec2.transformMat2d(extent, extent, xf);
    return vec2.subtract(out, extent, zero);
  },

  reciprocal: (out: vec2, v: vec2) => vec2.div(out, extraVec2Methods.unit(1), v),

  widthOverHeightAspectRatio: (v: vec2): number => extraVec2Methods.x(v) / extraVec2Methods.y(v),

  fromClientPosition: (event: {clientX: number, clientY: number}) => vec2.fromValues(event.clientX, event.clientY),
};

const enhancedVec2: typeof vec2 & typeof extraVec2Methods =
  Object.assign({}, vec2, extraVec2Methods);

const extraMat2dMethods = {
  a: (mat: mat2d) => mat[0],
  b: (mat: mat2d) => mat[1],
  c: (mat: mat2d) => mat[2],
  d: (mat: mat2d) => mat[3],
  tx: (mat: mat2d) => mat[4],
  ty: (mat: mat2d) => mat[5],
  setTxTy: (out: mat2d, mat: mat2d, txty: vec2) => {
    mat2d.copy(out, mat);
    out[4] = extraVec2Methods.x(txty);
    out[5] = extraVec2Methods.y(txty);
    return out;
  },
  components: (mat: mat2d): [number, number, number, number, number, number] => [
    extraMat2dMethods.a(mat),
    extraMat2dMethods.b(mat),
    extraMat2dMethods.c(mat),
    extraMat2dMethods.d(mat),
    extraMat2dMethods.tx(mat),
    extraMat2dMethods.ty(mat),
  ],

  toMat4: (mat: mat2d): mat4 => {
    return mat4.fromValues(
      extraMat2dMethods.a(mat),  extraMat2dMethods.b(mat),  extraMat2dMethods.tx(mat),  0,
      extraMat2dMethods.c(mat),  extraMat2dMethods.d(mat),  extraMat2dMethods.ty(mat),  0,
      0,  0,  1,  0,
      0,  0,  0,  1,
    );
  },

  // Scale factor without rotation - e.g. if the matrix is
  // a pure 90° rotation transform, this will produce a
  // unit vector.
  scaleFactor: (mat: mat2d): Vec2 => {
    const zero = extraVec2Methods.unit(0);
    const unitX = vec2.fromValues(1, 0);
    const unitY = vec2.fromValues(0, 1);
    vec2.transformMat2d(zero, zero, mat);
    vec2.transformMat2d(unitX, unitX, mat);
    vec2.transformMat2d(unitY, unitY, mat);
    return vec2.fromValues(
      vec2.distance(zero, unitX),
      vec2.distance(zero, unitY),
    );
  },

  toCSSInstruction: (mat: mat2d) => `matrix(${extraMat2dMethods.components(mat).join(', ')})`,
  toRNStylesInstruction: (mat: mat2d): TransformsStyle => ({
    transformMatrix: Array.from(extraMat4Methods.byColumn4x4(extraMat2dMethods.toMat4(mat))),
  }),

  byColumn3x3: (mat: mat2d): Float32Array => Float32Array.from([
    extraMat2dMethods.a(mat),
    extraMat2dMethods.b(mat),
    0,
    extraMat2dMethods.c(mat),
    extraMat2dMethods.d(mat),
    0,
    extraMat2dMethods.tx(mat),
    extraMat2dMethods.ty(mat),
    1,
  ]),

  byRow3x3: (mat: mat2d): Float32Array => Float32Array.from([
    extraMat2dMethods.a(mat),
    extraMat2dMethods.c(mat),
    extraMat2dMethods.tx(mat),
    extraMat2dMethods.b(mat),
    extraMat2dMethods.d(mat),
    extraMat2dMethods.ty(mat),
    0,
    0,
    1,
  ]),

  toComponents: (mat: mat2d): {a: number, b: number, c: number, d: number, tx: number, ty: number } => ({
    a: extraMat2dMethods.a(mat),
    b: extraMat2dMethods.b(mat),
    c: extraMat2dMethods.c(mat),
    d: extraMat2dMethods.d(mat),
    tx: extraMat2dMethods.tx(mat),
    ty: extraMat2dMethods.ty(mat),
  }),
  fromComponents: (c: {a: number, b: number, c: number, d: number, tx: number, ty: number }) => mat2d.fromValues(c.a, c.b, c.c, c.d, c.tx, c.ty),
  translation: (mat: mat2d) => vec2.fromValues(extraMat2dMethods.tx(mat), extraMat2dMethods.ty(mat)),
};

const enhancedMat2d: typeof mat2d & typeof extraMat2dMethods =
  Object.assign({}, mat2d, extraMat2dMethods);

const extraMat4Methods = {
  byColumn4x4: (mat: ReadonlyMat4): Float32Array => Float32Array.from(mat),
};

const enhancedMat4: typeof mat4 & typeof extraMat4Methods =
  Object.assign({}, mat4, extraMat4Methods);

export {enhancedVec2 as vec2, enhancedMat2d as mat2d, enhancedMat4 as mat4};

export type Vec2 = vec2;
export type Mat2d = mat2d;
